import { SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Space, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  prepareAccountLogin,
  socialAccountLoginCancel,
  socialAccountLoginFinish,
  socialAccountLoginQrcode,
  socialAccountLoginSms,
  socialAccountLoginStart,
  socialAccountLoginStatus,
} from '../api';
import { resolveErrorMessage } from '../utils/errorMessage';

const SUPPORTED = new Set(['douyin', 'kuaishou', 'weixin_channels']);
const AUTO_START_GUARD_TTL_MS = 120000;
const LOCAL_BRIDGE_BASE_URL = String(process.env.REACT_APP_PUBLISHY_AGENT_BRIDGE_URL || 'http://127.0.0.1:17861').replace(/\/+$/, '');
const LOCAL_BRIDGE_DEFAULT_TIMEOUT_MS = 4000;
const LOCAL_BRIDGE_START_TIMEOUT_MS = 20000;
const LOCAL_BRIDGE_FINISH_TIMEOUT_MS = 180000;
const DATA_IMAGE_PREFIX_RE = /^data:image\//i;
const HTTP_IMAGE_RE = /^(https?:\/\/|blob:)/i;
const BASE64_BODY_RE = /^[A-Za-z0-9+/=\s]+$/;
const autoStartGuardMap = new Map();

const normalizePlatformCode = (account) => String(account?.platform_code || '').trim();
const buildAutoStartGuardKey = (platformCode, accountId, autoStartToken, agentId, operatorId) => (
  `${platformCode || 'unknown'}::${accountId || 'unknown'}::${String(autoStartToken || 'default')}::${agentId || 'default'}::${operatorId || 'default'}`
);

const shouldSkipAutoStart = (guardKey) => {
  const now = Date.now();
  for (const [key, ts] of autoStartGuardMap.entries()) {
    if (!ts || now - ts > AUTO_START_GUARD_TTL_MS) autoStartGuardMap.delete(key);
  }
  if (autoStartGuardMap.has(guardKey)) return true;
  autoStartGuardMap.set(guardKey, now);
  return false;
};

const normalizeQrImage = (rawValue) => {
  const raw = String(rawValue || '').trim();
  if (!raw) return '';
  if (DATA_IMAGE_PREFIX_RE.test(raw)) return raw;
  if (HTTP_IMAGE_RE.test(raw)) return raw;
  const compact = raw.replace(/\s+/g, '');
  if (compact.length >= 128 && compact.length % 4 === 0 && BASE64_BODY_RE.test(compact)) {
    return `data:image/png;base64,${compact}`;
  }
  return '';
};

const requestLocalBridge = async (path, payload = {}, timeoutMs = LOCAL_BRIDGE_DEFAULT_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${LOCAL_BRIDGE_BASE_URL}${path}`, {
      method: 'POST',
      mode: 'cors',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload || {}),
    });
    const contentType = response.headers.get('content-type') || '';
    const result = contentType.includes('application/json')
      ? await response.json()
      : { status: response.ok ? 'success' : 'error', error_message: await response.text() };
    if (!response.ok) {
      throw new Error(resolveErrorMessage(result?.error_message, `本地 Agent 请求失败（HTTP ${response.status}）`));
    }
    return result;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('本地 Agent 请求超时，请确认 Agent 正在运行。');
    }
    throw error;
  } finally {
    window.clearTimeout(timer);
  }
};

const SocialAccountLoginButton = ({
  account,
  size = 'small',
  type = 'link',
  label = 'Agent登录',
  disabled = false,
  autoStart = false,
  autoStartToken = 'default',
  agentId = '',
  operatorId = '',
  onSuccess,
  buttonProps = {},
}) => {
  const navigate = useNavigate();
  const platformCode = normalizePlatformCode(account);
  const accountId = account?.account_id;
  const accountName = account?.account_name || accountId;
  const targetAgentId = String(agentId || '').trim();
  const targetOperatorId = String(operatorId || '').trim();

  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [qrImg, setQrImg] = useState('');
  const [statusText, setStatusText] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [smsRequired, setSmsRequired] = useState(false);
  const [smsPhoneMask, setSmsPhoneMask] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const [pendingConfirmReason, setPendingConfirmReason] = useState('');
  const [browserHeadless, setBrowserHeadless] = useState(true);
  const [finishError, setFinishError] = useState('');
  const [showRetryFinish, setShowRetryFinish] = useState(false);

  const pollRef = useRef(null);
  const autoStartedRef = useRef(false);
  const browserHeadlessRef = useRef(true);
  const loginDriverRef = useRef('legacy');
  const autoFinishPausedRef = useRef(false);

  const applyBrowserHeadless = (value) => {
    if (typeof value !== 'boolean') return;
    browserHeadlessRef.current = value;
    setBrowserHeadless(value);
  };

  const callLoginQrcodeApi = async (params = {}) => (
    loginDriverRef.current === 'agent'
      ? requestLocalBridge('/accounts/login/qrcode', params)
      : socialAccountLoginQrcode(params)
  );

  const callLoginStatusApi = async (params = {}) => (
    loginDriverRef.current === 'agent'
      ? requestLocalBridge('/accounts/login/status', params, 10000)
      : socialAccountLoginStatus(params)
  );

  const callLoginSmsApi = async (params = {}) => (
    loginDriverRef.current === 'agent'
      ? requestLocalBridge('/accounts/login/sms', params, 10000)
      : socialAccountLoginSms(params)
  );

  const callLoginFinishApi = async (params = {}) => (
    loginDriverRef.current === 'agent'
      ? requestLocalBridge('/accounts/login/finish', params, LOCAL_BRIDGE_FINISH_TIMEOUT_MS)
      : socialAccountLoginFinish(params)
  );

  const cleanupPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const closeModal = async () => {
    const sid = sessionId;
    const driver = loginDriverRef.current;
    cleanupPolling();
    setOpen(false);
    setQrImg('');
    setStatusText('');
    setFinishing(false);
    setSmsRequired(false);
    setSmsPhoneMask('');
    setSmsCode('');
    setPendingConfirmReason('');
    setFinishError('');
    setShowRetryFinish(false);
    autoFinishPausedRef.current = false;
    browserHeadlessRef.current = true;
    setBrowserHeadless(true);
    loginDriverRef.current = 'legacy';
    if (sid) {
      try {
        if (driver === 'agent') {
          await requestLocalBridge('/accounts/login/cancel', { session_id: sid }, 10000);
        } else {
          await socialAccountLoginCancel({ session_id: sid });
        }
      } catch (_) {
        // ignore
      }
    }
    setSessionId('');
  };

  useEffect(() => () => cleanupPolling(), []);

  useEffect(() => {
    if (!autoStart) return;
    if (!accountId || !SUPPORTED.has(platformCode)) return;
    if (autoStartedRef.current) return;
    const guardKey = buildAutoStartGuardKey(platformCode, accountId, autoStartToken, targetAgentId, targetOperatorId);
    if (shouldSkipAutoStart(guardKey)) return;
    autoStartedRef.current = true;
    Promise.resolve()
      .then(() => handleStart())
      .catch(() => {
        // ignore
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, autoStartToken, platformCode, accountId, targetAgentId, targetOperatorId]);

  if (!SUPPORTED.has(platformCode)) return null;

  const refreshQr = async (sidOverride) => {
    const isEventLike = sidOverride && typeof sidOverride === 'object' && 'nativeEvent' in sidOverride;
    const sid = (!isEventLike && typeof sidOverride === 'string' && sidOverride) ? sidOverride : sessionId;
    if (!sid) return;
    try {
      setStatusText('正在刷新二维码...');
      const resp = await callLoginQrcodeApi({ session_id: sid });
      const normalized = normalizeQrImage(resp?.qrcode_img);
      if (resp?.status === 'success' && normalized) {
        setQrImg(normalized);
        setStatusText('请使用手机扫码登录...');
      } else if (resp?.status === 'success') {
        setStatusText('二维码数据格式异常，请关闭后重试。');
        message.warning('二维码数据格式异常，请重试登录。');
      } else {
        setQrImg('');
        message.error(resolveErrorMessage(resp?.error_message, '刷新二维码失败'));
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '刷新二维码失败'));
    }
  };

  const finishIfLoggedIn = async (sidOverride, { autoTrigger = false } = {}) => {
    const sid = sidOverride || sessionId;
    if (!sid) return;
    setFinishError('');
    setShowRetryFinish(false);
    setFinishing(true);
    try {
      setStatusText('登录成功，正在保存 cookies 并同步到 OSS/ES...');
      const resp = await callLoginFinishApi({ session_id: sid });
      if (resp?.status === 'success') {
        autoFinishPausedRef.current = false;
        message.success('登录成功，cookies 已写回配置');
        await closeModal();
        if (typeof onSuccess === 'function') onSuccess(resp);
      } else {
        const errMsg = resolveErrorMessage(resp?.error_message, '保存 cookies 失败');
        setFinishError(errMsg);
        setShowRetryFinish(true);
        setStatusText(`cookies 保存失败：${errMsg}`);
        message.error(errMsg);
        if (autoTrigger) {
          autoFinishPausedRef.current = true;
          startPolling(sid);
        }
      }
    } catch (e) {
      const errMsg = resolveErrorMessage(e?.message || String(e), '保存 cookies 失败');
      setFinishError(errMsg);
      setShowRetryFinish(true);
      setStatusText(`cookies 保存失败：${errMsg}`);
      message.error(errMsg);
      if (autoTrigger) {
        autoFinishPausedRef.current = true;
        startPolling(sid);
      }
    } finally {
      setFinishing(false);
    }
  };

  const startPolling = (sid) => {
    cleanupPolling();
    if (!sid) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await callLoginStatusApi({ session_id: sid });
        if (s?.status !== 'success') return;
        applyBrowserHeadless(typeof s?.browser_headless === 'boolean' ? s.browser_headless : undefined);
        const st = String(s?.login_status || '').trim();
        if (st === 'logged_in') {
          if (autoFinishPausedRef.current) {
            setPendingConfirmReason('');
            setStatusText('登录已完成。上次 cookies 保存失败，请点击“重试保存 cookies”。');
            return;
          }
          cleanupPolling();
          setPendingConfirmReason('');
          await finishIfLoggedIn(sid, { autoTrigger: true });
          return;
        }
        if (st === 'sms_required') {
          setPendingConfirmReason('');
          const phoneMask = String(s?.sms_phone_mask || '').trim();
          setSmsPhoneMask(phoneMask);
          if (browserHeadlessRef.current) {
            cleanupPolling();
            setSmsRequired(true);
            setQrImg('');
            setStatusText('需要短信验证码，请在下方输入后继续');
          } else {
            setSmsRequired(false);
            setStatusText(
              phoneMask
                ? `检测到短信验证码（${phoneMask}），请在已打开的抖音登录窗口输入并提交，系统将自动继续。`
                : '检测到短信验证码，请在已打开的抖音登录窗口输入并提交，系统将自动继续。',
            );
          }
          return;
        }
        if (st === 'blocked') {
          cleanupPolling();
          setPendingConfirmReason('');
          setStatusText(`登录被平台拦截（${s?.blocked_reason || '风控/验证码'}），请稍后重试或人工处理`);
          return;
        }
        if (st === 'failed') {
          cleanupPolling();
          setPendingConfirmReason('');
          const reason = resolveErrorMessage(s?.error, '未知原因');
          setStatusText(`检测失败：${reason}`);
          return;
        }
        if (st === 'pending_confirm') {
          const timeoutReached = Boolean(s?.pending_confirm_timeout_reached);
          const timeoutReason = String(s?.pending_confirm_timeout_reason || '').trim();
          if (timeoutReached) {
            setPendingConfirmReason(timeoutReason || '已扫码但长时间未完成登录，请在手机微信完成确认后重试。');
          } else {
            setPendingConfirmReason('');
          }
          setStatusText(String(s?.login_hint || '已扫码，请在手机上确认登录...'));
          return;
        }
        setPendingConfirmReason('');
        const hint = String(s?.login_hint || '').trim();
        setStatusText(hint || '请使用手机扫码登录...');
      } catch (_) {
        // ignore transient
      }
    }, 2500);
  };

  const submitSms = async () => {
    if (!sessionId) return;
    const code = String(smsCode || '').trim();
    if (!code) {
      message.warning('请输入短信验证码');
      return;
    }
    setSmsSubmitting(true);
    try {
      const resp = await callLoginSmsApi({ session_id: sessionId, sms_code: code });
      if (resp?.status !== 'success') {
        message.error(resolveErrorMessage(resp?.error_message, '验证码提交失败'));
        return;
      }
      setSmsRequired(false);
      setSmsPhoneMask('');
      setSmsCode('');
      setStatusText('验证码已提交，正在验证登录状态...');
      startPolling(sessionId);
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '验证码提交失败'));
    } finally {
      setSmsSubmitting(false);
    }
  };

  const handleLegacyStart = async () => {
    if (!accountId) {
      message.error('缺少 account_id');
      return;
    }
    try {
      loginDriverRef.current = 'legacy';
      const params = { account_id: accountId };
      if (targetAgentId) params.agent_id = targetAgentId;
      if (targetOperatorId) params.operator_id = targetOperatorId;
      const resp = await socialAccountLoginStart(params);
      if (resp?.status !== 'success') {
        message.error(resolveErrorMessage(resp?.error_message, '启动扫码登录失败'));
        return;
      }
      const sid = String(resp?.session_id || '');
      if (!sid) {
        message.error('启动扫码登录失败：缺少 session_id');
        return;
      }
      applyBrowserHeadless(typeof resp?.browser_headless === 'boolean' ? resp.browser_headless : true);
      setSessionId(sid);
      const img = normalizeQrImage(resp?.qrcode_img);
      if (img) setQrImg(img);
      setFinishError('');
      setShowRetryFinish(false);
      autoFinishPausedRef.current = false;
      setOpen(true);
      setStatusText('请使用手机扫码登录...');
      setPendingConfirmReason('');
      startPolling(sid);
      if (!img) {
        setTimeout(() => {
          refreshQr(sid);
        }, 1200);
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '启动扫码登录失败'));
    }
  };

  const handleAgentStartError = (error) => {
    const rawMessage = String(error?.message || error || '').trim();
    if (!rawMessage) {
      message.error('通过 Agent 发起登录失败');
      return;
    }
    if (rawMessage.includes('agent_account_mismatch')) {
      Modal.info({
        title: '请在绑定设备上发起登录',
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>当前浏览器连接到的本地 Agent 不是该账号绑定的设备。</div>
            <div style={{ color: '#999' }}>请在已绑定这条账号的那台电脑上打开 Publishy 页面，再发起登录。</div>
          </div>
        ),
        okText: '知道了',
      });
      return;
    }
    if (
      rawMessage.includes('agent_not_paired')
      || rawMessage.includes('Failed to fetch')
      || rawMessage.includes('NetworkError')
      || rawMessage.includes('本地 Agent 请求超时')
    ) {
      openAgentGuide(
        '先连接本机 Agent',
        '当前浏览器无法连接到可用的本地 Publishy Agent。请确认你正在绑定设备上访问、Agent 已启动，且当前站点已加入 Agent 允许来源（bridge allowed origins）。',
      );
      return;
    }
    if (rawMessage.includes('origin_not_allowed')) {
      Modal.info({
        title: '当前站点未被本机 Agent 允许',
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>本机 Agent 已启动，但拒绝了当前网页域名跨域请求。</div>
            <div style={{ color: '#999' }}>请在 Agent 配置中把当前站点加入 `PUBLISHY_AGENT_BRIDGE_ALLOWED_ORIGINS` 后重启 Agent。</div>
          </div>
        ),
        okText: '知道了',
      });
      return;
    }
    message.error(resolveErrorMessage(rawMessage, '通过 Agent 发起登录失败'));
  };

  const handleAgentStart = async () => {
    if (!accountId) {
      message.error('缺少 account_id');
      return;
    }
    try {
      const resp = await requestLocalBridge(
        '/accounts/login/start',
        {
          account_id: accountId,
          agent_id: targetAgentId || undefined,
          operator_id: targetOperatorId || undefined,
        },
        LOCAL_BRIDGE_START_TIMEOUT_MS,
      );
      if (resp?.status !== 'success') {
        message.error(resolveErrorMessage(resp?.error_message, '通过 Agent 发起登录失败'));
        return;
      }
      const sid = String(resp?.session_id || '');
      if (!sid) {
        message.error('通过 Agent 发起登录失败：缺少 session_id');
        return;
      }
      loginDriverRef.current = 'agent';
      applyBrowserHeadless(typeof resp?.browser_headless === 'boolean' ? resp.browser_headless : true);
      setSessionId(sid);
      const img = normalizeQrImage(resp?.qrcode_img);
      if (img) setQrImg(img);
      setFinishError('');
      setShowRetryFinish(false);
      autoFinishPausedRef.current = false;
      setOpen(true);
      setStatusText('请使用手机扫码登录...');
      setPendingConfirmReason('');
      startPolling(sid);
      if (!img) {
        setTimeout(() => {
          refreshQr(sid);
        }, 1200);
      }
    } catch (error) {
      loginDriverRef.current = 'legacy';
      handleAgentStartError(error);
    }
  };

  const openAgentGuide = (title, description) => {
    Modal.confirm({
      title,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>{description}</div>
          <div style={{ color: '#999' }}>建议路径：Agent管理 -> 检测 Agent -> 绑定设备 -> 回到账号页继续登录。</div>
        </div>
      ),
      okText: '前往Agent管理',
      cancelText: '取消',
      onOk: () => navigate('/agent/setup'),
    });
  };

  const openInfoGuide = (title, description) => {
    Modal.info({
      title,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>{description}</div>
          <div style={{ color: '#999' }}>当前按钮已经切换为 Agent 主路径入口，旧链路只在兼容环境下回退。</div>
        </div>
      ),
      okText: '知道了',
    });
  };

  const handleStart = async () => {
    if (!accountId) {
      message.error('缺少 account_id');
      return;
    }

    setStarting(true);
    try {
      const prepareParams = { account_id: accountId };
      if (targetAgentId) prepareParams.agent_id = targetAgentId;
      if (targetOperatorId) prepareParams.operator_id = targetOperatorId;
      const prepareResp = await prepareAccountLogin(prepareParams);
      if (prepareResp?.status !== 'success') {
        message.error(resolveErrorMessage(prepareResp?.error_message, '登录准备检查失败'));
        return;
      }

      const loginPlan = prepareResp?.login || {};
      const readiness = loginPlan?.readiness || {};
      const launchMode = String(loginPlan?.launch_mode || '').trim().toLowerCase();
      const nextActionType = String(readiness?.next_action?.type || '').trim().toLowerCase();
      const guideMessage = String(loginPlan?.message || readiness?.message || '').trim();

      if (nextActionType === 'bind_agent') {
        openAgentGuide('先绑定 Agent', guideMessage || '该账号尚未绑定 Publishy Agent。');
        return;
      }
      if (nextActionType === 'start_agent') {
        if (launchMode === 'agent' || loginPlan?.agent_login_runner_enabled === true) {
          await handleAgentStart();
          return;
        }
        openAgentGuide('先启动 Agent', guideMessage || '该账号已绑定 Agent，但对应设备当前离线。');
        return;
      }
      if (launchMode === 'publish_ready') {
        message.success(guideMessage || '当前账号登录态有效，可直接发布');
        if (typeof onSuccess === 'function') {
          await Promise.resolve(onSuccess({ status: 'success', account_id: accountId, mode: 'publish_ready' }));
        }
        return;
      }
      if (launchMode === 'legacy_compat') {
        if (!autoStart) {
          message.info(guideMessage || '当前环境将临时回退到兼容登录链路。');
        }
        loginDriverRef.current = 'legacy';
        await handleLegacyStart();
        return;
      }
      if (launchMode === 'agent') {
        await handleAgentStart();
        return;
      }
      openInfoGuide(
        '当前无法发起登录',
        guideMessage || '当前环境不能使用兼容链路，且 Agent 登录执行器尚未接通。',
      );
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '登录准备检查失败'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <Button
        type={type}
        size={size}
        icon={<SafetyCertificateOutlined />}
        onClick={handleStart}
        loading={starting}
        disabled={disabled || starting || finishing}
        {...buttonProps}
      >
        {label}
      </Button>

      <Modal
        title={`扫码登录：${accountName || '账号'}（${platformCode}）`}
        open={open}
        onCancel={closeModal}
        footer={(
          <Space>
            <Button onClick={closeModal}>
              关闭
            </Button>
          </Space>
        )}
      >
        {smsRequired ? (
          <div style={{
            minHeight: 220,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
          >
            <div style={{ color: '#333', marginBottom: 8 }}>
              {statusText || '需要短信验证码，请在下方输入后继续'}
            </div>
            {smsPhoneMask && (
              <div style={{ color: '#666', marginBottom: 12 }}>验证码手机号：{smsPhoneMask}</div>
            )}
            <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 360 }}>
              <input
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
                placeholder="请输入短信验证码"
                style={{
                  flex: 1,
                  height: 32,
                  padding: '0 8px',
                  borderRadius: 6,
                  border: '1px solid #d9d9d9',
                }}
              />
              <Button type="primary" onClick={submitSms} loading={smsSubmitting}>
                提交
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 260,
              height: 260,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
            }}
            >
              {qrImg ? (
                <img
                  src={qrImg}
                  alt="social_login_qrcode"
                  style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: 240,
                    maxHeight: 240,
                    objectFit: 'contain',
                    objectPosition: 'center',
                  }}
                />
              ) : (
                <div style={{ color: '#999' }}>二维码加载中...</div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#333', marginBottom: 8 }}>扫码后会自动检测登录状态，并同步 cookies。</div>
              <div style={{ color: '#999' }}>{statusText || '-'}</div>
              {showRetryFinish && sessionId && (
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    type="primary"
                    ghost
                    loading={finishing}
                    onClick={() => {
                      cleanupPolling();
                      autoFinishPausedRef.current = false;
                      finishIfLoggedIn(sessionId, { autoTrigger: false });
                    }}
                  >
                    重试保存 cookies
                  </Button>
                </div>
              )}
              {finishError && (
                <div style={{ marginTop: 10 }}>
                  <Alert
                    showIcon
                    type="error"
                    message="cookies 同步失败"
                    description={finishError}
                  />
                </div>
              )}
              {!browserHeadless && smsPhoneMask && (
                <div style={{ color: '#999', marginTop: 8 }}>验证码手机号：{smsPhoneMask}</div>
              )}
              {pendingConfirmReason && (
                <div style={{ marginTop: 10 }}>
                  <Alert
                    showIcon
                    type="warning"
                    message="手机确认超时"
                    description={pendingConfirmReason}
                  />
                </div>
              )}
              {finishing && <div style={{ color: '#999', marginTop: 8 }}>正在上传 OSS 并回写配置...</div>}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SocialAccountLoginButton;
